import React from 'react';
import { ConfirmDialog, FilterChip } from '@parsina/framework';
import { CalendarClock } from 'lucide-react';
import { C, CT } from '../../../constants/theme';
import { FOLLOW_UP_INTERVALS, DEFAULT_FOLLOW_UP_INTERVAL } from '../Visit.shared.js';

// «پایان جلسه» — and, when the visit leaves work undone, «نوبت بعدی».
//
// ── The gap this closes ─────────────────────────────────────────────────────
// D17 gave a plan line three stages: seen → planned → performed. A dentist who
// adds «ترمیم ۲۷» and does not perform it has told the record that the patient
// must come back — and told nobody who could act on it. The visit ended, the
// patient walked past reception, and the next appointment was booked only if
// somebody happened to remember.
//
// So the one moment the dentist is certainly thinking about it — the moment
// they close the visit — is where they are asked.
//
// ── It asks only when there is something to ask about ──────────────────────
// A visit that finished everything gets exactly the dialog it had before. An
// extra question after a completed course is a question about nothing, and a
// dialog that always asks is one that is always dismissed.
//
// ── Deadlines, in the words a dentist uses ─────────────────────────────────
// «همین امروز», «فردا», «کمتر از یک هفته» — not «۲ هفتهٔ دیگر», which nobody
// says. Every option but the first two is a DEADLINE: «کمتر از یک هفته» means
// book WITHIN a week, and the desk may book it this afternoon.
//
// That is why a choice carries a window rather than an instant. Collapsed into
// one date, «کمتر از یک هفته» would mean «not before a week» — the opposite
// instruction, so the more urgent the dentist was the longer the patient would
// wait. An exact date is still not offered: the desk owns the calendar, the
// patient's availability and the money conversation, and what travels is an
// intent.
//
// ── «لازم نیست» is a real answer ────────────────────────────────────────────
// Not the same as saying nothing. It raises no prompt AND withdraws one an
// earlier visit left, because the clinical judgement has just changed. Saying
// nothing — a client that never shows this, a visit closed from the desk —
// leaves the work outstanding and the desk is told anyway.

export default function FinishVisitDialog({
  open, onOpenChange, onConfirm, loading, t, formatNumber,
  // §15.1's selector contract is `{page}-{region}-{element}`, and only the
  // CALLER knows which page it is on — so the two buttons' testids are props,
  // exactly as the kit's ConfirmDialog takes them. The phone's suite selects on
  // them; a confirm that can only be found by matching Persian copy is a test
  // that breaks the next time the copy is rewritten.
  testId, confirmTestId, cancelTestId,
  // The lines this visit is about to leave undone. Empty ⇒ no question.
  outstanding = [],
  // The chosen interval key, owned by the caller so it survives a re-render
  // while the mutation is in flight.
  interval, onIntervalChange,
}) {
  const asks = outstanding.length > 0;

  // Reset to the default on every open: the dialog is kept mounted by the
  // screen, so last visit's «لازم نیست» would otherwise greet the next patient.
  React.useEffect(() => {
    if (open) onIntervalChange(DEFAULT_FOLLOW_UP_INTERVAL);
  }, [open, onIntervalChange]);

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('visit.finish.confirm.title')}
      description={(
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.8, color: C.textSecondary }}>
            {t('visit.finish.confirm.body')}
          </p>

          {asks && (
            <div
              data-testid="finish-follow-up"
              style={{
                border: `1px solid ${C.border}`, borderRadius: 10,
                padding: '10px 12px', background: CT.surface2,
              }}
            >
              {/* WHAT is outstanding, named. «۲ اقدام باقی مانده» on its own
                  makes a dentist reopen the plan to find out which, and this
                  dialog is the last thing standing between them and a closed
                  visit. */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontSize: 12.5, fontWeight: 700, color: C.textPrimary,
              }}>
                <CalendarClock size={14} aria-hidden />
                {t('visit.finish.followUp.title', {
                  count: formatNumber(outstanding.length),
                })}
              </div>
              <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 4 }}>
                {outstanding.slice(0, 3).map((item) => (
                  item.toothFdi
                    ? `${item.description || t('visit.plan.untitledItem')} — ${t('visit.record.tooth')} ${formatNumber(item.toothFdi)}`
                    : (item.description || t('visit.plan.untitledItem'))
                )).join(' · ')}
                {outstanding.length > 3
                  ? ` ${t('visit.finish.followUp.andMore', {
                    count: formatNumber(outstanding.length - 3),
                  })}`
                  : ''}
              </div>

              <div style={{ fontSize: 12, color: C.textSecondary, margin: '10px 0 6px' }}>
                {t('visit.finish.followUp.when')}
              </div>
              {/* CHIPS, not the kit's ToggleGroup.
                  ToggleGroup is ONE non-wrapping row of `flex-1` segments, so
                  seven options split the dialog's width seven ways and «کمتر از
                  دو هفته» had about forty pixels to say itself in. Chips take
                  the width of their own words and wrap onto a second line,
                  which is what an answer the dentist has to READ needs.

                  Same control, same one-of-seven behaviour: `aria-pressed` and
                  a single active chip. */}
              <div
                role="group"
                aria-label={t('visit.finish.followUp.when')}
                data-testid="finish-follow-up-intervals"
                style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
              >
                {FOLLOW_UP_INTERVALS.map((i) => (
                  <FilterChip
                    key={i.key}
                    active={interval === i.key}
                    onClick={() => onIntervalChange(i.key)}
                    data-testid={`finish-follow-up-${i.key}`}
                    label={t(`visit.finish.followUp.${i.key}`)}
                  />
                ))}
              </div>

              {/* Said plainly, because a dentist pressing this has no other way
                  to know it reached anybody: what they choose becomes a row on
                  the reception desk, not a note in the chart. */}
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 8 }}>
                {interval === 'none'
                  ? t('visit.finish.followUp.noneNote')
                  : t('visit.finish.followUp.deskNote')}
              </div>
            </div>
          )}
        </div>
      )}
      confirmLabel={t('visit.finish.action')}
      cancelLabel={t('common.actions.cancel')}
      loading={loading}
      onConfirm={onConfirm}
      data-testid={testId || 'finish-visit-dialog'}
      confirmTestId={confirmTestId}
      cancelTestId={cancelTestId}
    />
  );
}

/** The toast after a close, which depends on what the desk was told. */
export function finishToast(t, outcome) {
  switch (outcome) {
    case 'raised':
    case 'refreshed':
      return t('visit.finish.done.followUpRaised');
    case 'already_booked':
      return t('visit.finish.done.alreadyBooked');
    // «unavailable» is NOT «nothing to do»: the prompt could not be raised and a
    // patient may have been dropped, so it says so rather than staying quiet.
    case 'unavailable':
      return t('visit.finish.done.followUpFailed');
    default:
      return t('visit.finish.done.plain');
  }
}
